import { useCallback, useRef } from 'react';

/**
 * Loader suspense related hooks.
 *
 * @name loaderSuspenseHooks
 * @memberof Mojito.Presentation
 */

/**
 * The hook defines the visibility state of a loader component. Handy to be used on a views that are waiting for
 * content load from a server. The hook returns true if isContentPending is true and if min show up delay is not over.
 * By default, hook supports only one time loader appearance per component life cycle which means that it reacts on one change of isContentPending flag from true to false.
 * In order to show loader each time isContentPending is set to true, the unique contentHash argument should be provided on each new loading cycle.
 *
 * @function useLoaderVisibility
 *
 * @param {boolean} isContentPending - True if content is in still in pending state. Hence, the loader should be visible.
 * @param {*} [contentHash] - Content hash used to identify the content type change and force loader to be shown again.
 *
 * @returns {boolean} True if loader should be shown, otherwise false.
 * @memberof Mojito.Presentation.loaderSuspenseHooks
 */
export const useLoaderVisibility = (isContentPending, contentHash) => {
    const [contentCache, addContentHash] = useContentCache();

    const isNeverExistedContent = contentCache[contentHash] === undefined;
    const isNeverLoadedContent = isNeverExistedContent || contentCache[contentHash] === false;
    const isPendingNeverLoadedContent = isContentPending && isNeverLoadedContent;
    const isContentReadyOnMount = !isContentPending && isNeverExistedContent;
    // Storing content loading state in a cache to not show loader for it any more even if content is being reload again.
    if (isNeverExistedContent || !isContentPending) {
        addContentHash(contentHash, !isContentPending);
    }

    // Need to display loader if content is still loading for the first time and if min loader timeout is not over.
    return isPendingNeverLoadedContent && !isContentReadyOnMount;
};

const useContentCache = () => {
    const cacheRef = useRef({});
    const add = useCallback((hash, isLoaded) => {
        cacheRef.current[hash] = isLoaded;
    }, []);
    return [cacheRef.current, add];
};
